import { z } from 'zod'
import { toolDefinition } from '@tanstack/ai'
import {
  createEventAwareBindings,
  toolsToBindings,
} from './bindings/tool-to-binding'
import { stripTypeScript } from './strip-typescript'
import { warnIfBindingsExposeSecrets } from './validate-bindings'
import type { ServerTool, ToolExecutionContext } from '@tanstack/ai'
import type {
  CodeModeTool,
  CodeModeToolConfig,
  CodeModeToolResult,
  IsolateContext,
} from './types'

/**
 * Schema for the execute_typescript tool input
 */
const executeTypescriptInputSchema = z.object({
  typescriptCode: z
    .string()
    .describe(
      'TypeScript code to execute in the sandbox. ' +
        'Use external_* functions to call available APIs. ' +
        'Return a value to pass results back.',
    ),
})

/**
 * Schema for the execute_typescript tool output
 */
const executeTypescriptOutputSchema = z.object({
  success: z.boolean().describe('Whether execution completed without errors'),
  result: z
    .unknown()
    .optional()
    .describe('Return value from the executed code'),
  logs: z
    .array(z.string())
    .optional()
    .describe('Console output captured during execution'),
  error: z
    .object({
      message: z.string(),
      name: z.string().optional(),
      line: z.number().optional(),
      stack: z.string().optional(),
    })
    .optional()
    .describe('Error details if execution failed'),
})

export type ExecuteTypescriptInput = z.infer<
  typeof executeTypescriptInputSchema
>
export type ExecuteTypescriptOutput = z.infer<
  typeof executeTypescriptOutputSchema
>

/**
 * Create an execute_typescript tool that can be used alongside other agent tools.
 *
 * This tool allows an LLM to execute TypeScript code in a secure sandbox.
 * Tools passed in the config become `external_*` functions available inside the sandbox.
 *
 * @example
 * ```typescript
 * import { createCodeMode } from '@tanstack/ai-code-mode'
 * import { createNodeIsolateDriver } from '@tanstack/ai-isolate-node'
 *
 * const { tool, systemPrompt } = createCodeMode({
 *   driver: createNodeIsolateDriver(),
 *   tools: [weatherTool, dbTool],  // Become external_fetchWeather, external_dbQuery
 *   timeout: 30000,
 * })
 *
 * chat({
 *   systemPrompts: [myPrompt, systemPrompt],
 *   tools: [tool, searchTool, emailTool],
 *   messages,
 * })
 * ```
 */
export function createCodeModeTool(
  config: CodeModeToolConfig,
): ServerTool<
  typeof executeTypescriptInputSchema,
  typeof executeTypescriptOutputSchema,
  'execute_typescript'
> {
  const {
    driver,
    tools,
    timeout = 30000,
    memoryLimit = 128,
    getSnippetBindings,
    onSecretParameter,
    transpile = stripTypeScript,
  } = config

  // Validate tools
  if (tools.length === 0) {
    throw new Error('At least one tool must be provided to createCodeModeTool')
  }

  // Transform tools to bindings with external_ prefix (static bindings)
  const staticBindings = toolsToBindings(tools, 'external_')

  // Shared across static + dynamic (snippet) binding scans so a given
  // (toolName, paramPath) pair surfaces at most once per code-mode instance.
  const secretDedupCache = new Set<string>()

  warnIfBindingsExposeSecrets(Object.values(staticBindings), {
    handler: onSecretParameter,
    dedupCache: secretDedupCache,
  })

  // Create the tool definition
  const definition = toolDefinition({
    name: 'execute_typescript' as const,
    description: buildToolDescription(tools),
    inputSchema: executeTypescriptInputSchema,
    outputSchema: executeTypescriptOutputSchema,
  })

  // Return server tool with execute function that accepts context
  return definition.server(
    async (
      input,
      toolContext?: ToolExecutionContext,
    ): Promise<CodeModeToolResult> => {
      const { typescriptCode } = input
      const startedAt = Date.now()

      // Get emitCustomEvent from context or use no-op
      const emitCustomEvent = toolContext?.emitCustomEvent || (() => {})

      const finish = (
        result: CodeModeToolResult,
        phase: string,
      ): CodeModeToolResult => {
        const durationMs = Date.now() - startedAt
        const payload = {
          timestamp: Date.now(),
          durationMs,
          phase,
          success: result.success,
          logCount: result.logs?.length ?? 0,
          error: result.error
            ? {
                name: result.error.name,
                message: result.error.message,
                ...(result.error.stack !== undefined && {
                  stack: result.error.stack,
                }),
                ...(result.error.line !== undefined && {
                  line: result.error.line,
                }),
              }
            : undefined,
        }
        emitCustomEvent('code_mode:execution_finished', payload)
        if (!result.success) {
          console.error('[code-mode] execute_typescript failed', payload)
        } else if (
          typeof process !== 'undefined' &&
          process.env?.CODE_MODE_DEBUG === '1'
        ) {
          console.info('[code-mode] execute_typescript ok', {
            durationMs,
            phase,
            logCount: payload.logCount,
          })
        }
        return result
      }

      if (!typescriptCode || typeof typescriptCode !== 'string') {
        return finish(
          {
            success: false,
            error: {
              message: 'typescriptCode must be a non-empty string',
              name: 'ValidationError',
            },
          },
          'validate-input',
        )
      }

      // Create a fresh sandbox context for this execution
      let isolateContext: IsolateContext | null = null

      // Emit execution started event immediately
      emitCustomEvent('code_mode:execution_started', {
        timestamp: Date.now(),
        codeLength: typescriptCode.length,
      })

      try {
        // Step 1: Strip TypeScript (also serves as syntax validation via the
        // transpiler — sucrase by default, or a user-supplied `transpile`)
        let strippedCode: string
        try {
          strippedCode = await transpile(typescriptCode)
        } catch (error) {
          // Type/syntax error from the transpiler
          return finish(
            {
              success: false,
              error: {
                message: error instanceof Error ? error.message : String(error),
                name: 'TypeScriptError',
                ...(error instanceof Error &&
                  error.stack !== undefined && { stack: error.stack }),
              },
            },
            'transpile',
          )
        }

        // Step 2: Get dynamic snippet bindings if available
        const snippetBindings = getSnippetBindings
          ? await getSnippetBindings()
          : {}

        // Scan dynamic bindings too — their schemas are equally in-scope for
        // the same exfiltration threat. Dedup cache prevents repeat warnings
        // when the same binding reappears across executions.
        const snippetBindingValues = Object.values(snippetBindings)
        if (snippetBindingValues.length > 0) {
          warnIfBindingsExposeSecrets(snippetBindingValues, {
            handler: onSecretParameter,
            dedupCache: secretDedupCache,
          })
        }

        // Step 3: Merge static and dynamic bindings, then wrap with event awareness
        const allBindings = { ...staticBindings, ...snippetBindings }
        const eventAwareBindings = createEventAwareBindings(
          allBindings,
          emitCustomEvent,
        )

        // Step 4: Create sandbox context with event-aware bindings
        try {
          isolateContext = await driver.createContext({
            bindings: eventAwareBindings,
            timeout,
            memoryLimit,
          })
        } catch (error) {
          return finish(
            {
              success: false,
              error: {
                message: error instanceof Error ? error.message : String(error),
                name:
                  error instanceof Error ? error.name : 'CreateContextError',
                ...(error instanceof Error &&
                  error.stack !== undefined && { stack: error.stack }),
              },
            },
            'create-context',
          )
        }

        // Step 5: Execute the code in the sandbox
        const executionResult = await isolateContext.execute(strippedCode)

        // Emit console logs as custom events
        if (executionResult.logs && executionResult.logs.length > 0) {
          for (const log of executionResult.logs) {
            // Parse log level from prefix (added by sandbox console implementation)
            let level: 'log' | 'warn' | 'error' | 'info' = 'log'
            let message = log

            if (log.startsWith('ERROR: ')) {
              level = 'error'
              message = log.slice(7)
            } else if (log.startsWith('WARN: ')) {
              level = 'warn'
              message = log.slice(6)
            } else if (log.startsWith('INFO: ')) {
              level = 'info'
              message = log.slice(6)
            }

            emitCustomEvent('code_mode:console', {
              level,
              message,
              timestamp: Date.now(),
            })
          }
        }

        if (executionResult.success) {
          return finish(
            {
              success: true,
              result: executionResult.value,
              logs: executionResult.logs,
            },
            'execute',
          )
        }

        return finish(
          {
            success: false,
            error: executionResult.error
              ? {
                  message: executionResult.error.message,
                  name: executionResult.error.name,
                  ...(executionResult.error.stack !== undefined && {
                    stack: executionResult.error.stack,
                  }),
                }
              : { message: 'Unknown execution error', name: 'UnknownError' },
            logs: executionResult.logs,
          },
          'execute',
        )
      } catch (error) {
        return finish(
          {
            success: false,
            error: {
              message: error instanceof Error ? error.message : String(error),
              name: error instanceof Error ? error.name : 'Error',
              ...(error instanceof Error &&
                error.stack !== undefined && { stack: error.stack }),
            },
          },
          'unhandled',
        )
      } finally {
        // Always clean up the sandbox context
        if (isolateContext) {
          await isolateContext.dispose()
        }
      }
    },
  )
}

/**
 * Build the tool description including available external functions
 */
function buildToolDescription(tools: Array<CodeModeTool>): string {
  const eager = tools.filter((t) => !t.lazy)
  const hasLazy = tools.some((t) => t.lazy)
  const externalFunctions = eager.map((t) => `external_${t.name}`).join(', ')

  const discoverable = hasLazy
    ? ` Additional functions can be discovered via the discover_tools tool.`
    : ''

  return (
    `Execute TypeScript code in a secure sandbox environment. ` +
    `The code can use these external API functions: ${externalFunctions}.${discoverable} ` +
    `All external_* calls are async and must be awaited. ` +
    `Return a value to pass results back. Use console.log() for debugging.`
  )
}
